// A promoted user (LocalSigningShadow::Promotion) signs on their device while Treechat
// still holds the wallet. A browser can lose its share and keep its device id: the share
// lives in IndexedDB, which a browser that refused persistent storage may evict, while
// the id also sits in localStorage. Requests then keep naming a device that can't sign.
// On load: set the share up again from Treechat's copy, and when that fails, forget the
// device: drop its unusable share (Treechat keeps the wallet) and its id, so requests go
// without one and Treechat signs them. Share readers re-save the id of any share left.

export type KeyState = 'present' | 'missing' | 'legacy' | 'unknown'

export interface PromotedDeviceRecoveryDeps {
    storedDeviceId: () => string | null
    checkKeys: () => Promise<KeyState>
    setUpKeys: () => Promise<boolean>
    forgetDevice: () => Promise<void>
    signalReady: () => void
    failedThisSession: () => boolean
    setFailed: (failed: boolean) => void
    report: (fields: Record<string, string | boolean | null>) => Promise<void>
}

export type RecoveryOutcome = 'present' | 'unknown' | 'recovered' | 'device_id_cleared'

export async function recoverPromotedDeviceKeys(
    deps: PromotedDeviceRecoveryDeps,
): Promise<RecoveryOutcome> {
    const state = await deps.checkKeys()
    // 'unknown': Treechat couldn't be reached; leave everything and look again next load.
    if (state === 'present' || state === 'unknown') return state

    const storedDeviceId = deps.storedDeviceId()
    if (!deps.failedThisSession()) {
        const recovered = await deps.setUpKeys()
        deps.setFailed(!recovered)
        if (recovered) {
            deps.signalReady()
            await deps.report({
                source: 'promoted_recovery',
                ok: true,
                reason: `keys were ${state}`,
                stored_device: Boolean(storedDeviceId),
            })
            return 'recovered'
        }
    }

    await deps.forgetDevice()
    await deps.report({
        source: 'promoted_recovery',
        ok: false,
        reason: `keys were ${state}; device id cleared so Treechat signs`,
        stored_device: Boolean(storedDeviceId),
    })
    return 'device_id_cleared'
}
